/**
 * CrmFitSlider — a discrete 5-point rating control (Complexity / Portfolio value / Client style
 * on the Opportunity detail). Ported from the legacy opportunity_details.html Fit assessment card:
 * click a dot to jump straight to it, or drag anywhere on the track to scrub. Mirrors that tool's
 * save behavior too — dragging only updates the LOCAL visual position; the actual onChange (and
 * therefore the Airtable write) fires once on release or on a plain click, never per pixel of drag,
 * so scrubbing across the track doesn't fire a flood of PATCH requests.
 */
import { useRef, useState } from 'react'
import { fitColor } from '../../services/opportunities.js'

export default function CrmFitSlider({ label, options, value, onChange, disabled = false, colorFn }) {
  const colorOf = colorFn || fitColor
  const trackRef = useRef(null)
  const draggingRef = useRef(false)
  const [liveIdx, setLiveIdx] = useState(null) // non-null only while actively dragging

  const committedIdx = options.indexOf(value)
  const idx = liveIdx != null ? liveIdx : committedIdx

  function idxFromClientX(clientX) {
    const rect = trackRef.current.getBoundingClientRect()
    const pct = (clientX - rect.left) / rect.width
    return Math.max(0, Math.min(options.length - 1, Math.round(pct * (options.length - 1))))
  }

  function onTrackPointerDown(e) {
    if (disabled) return
    draggingRef.current = true
    trackRef.current.setPointerCapture(e.pointerId)
    setLiveIdx(idxFromClientX(e.clientX))
  }
  function onTrackPointerMove(e) {
    if (!draggingRef.current || disabled) return
    setLiveIdx(idxFromClientX(e.clientX))
  }
  function endDrag(e) {
    if (!draggingRef.current) return
    draggingRef.current = false
    try { trackRef.current.releasePointerCapture(e.pointerId) } catch (err) {}
    setLiveIdx(cur => {
      if (cur != null && options[cur] !== value) onChange(options[cur])
      return null
    })
  }

  function clickDot(e, i) {
    e.stopPropagation()
    if (disabled) return
    setLiveIdx(null)
    if (options[i] !== value) onChange(options[i])
  }

  const color = idx >= 0 ? colorOf(options[idx]) : null
  const pct = idx >= 0 && options.length > 1 ? (idx / (options.length - 1)) * 100 : 0

  return (
    <div style={S.row}>
      <div style={S.hdr}>
        <span style={S.lbl}>{label}</span>
        <span style={{ ...S.val, color: color || 'var(--aj-text-mid)' }}>{idx >= 0 ? options[idx] : 'Not set'}</span>
      </div>
      <div ref={trackRef} style={{ ...S.trackWrap, cursor: disabled ? 'default' : 'pointer' }}
        onPointerDown={onTrackPointerDown} onPointerMove={onTrackPointerMove}
        onPointerUp={endDrag} onPointerCancel={endDrag}>
        <div style={S.track}>
          <div style={{ ...S.fill, width: pct + '%', background: color || 'var(--aj-border-mid)' }} />
        </div>
        <div style={S.dotsRow}>
          {options.map((opt, i) => (
            <div key={opt} title={opt} onPointerDown={e => clickDot(e, i)}
              style={{
                ...S.dot,
                width: i === idx ? 10 : 6, height: i === idx ? 10 : 6,
                background: i <= idx ? (color || 'var(--aj-border-mid)') : 'var(--aj-border-mid)',
              }} />
          ))}
        </div>
      </div>
    </div>
  )
}

const S = {
  row: { padding: '10px 2px', borderBottom: '0.5px solid var(--aj-border)' },
  hdr: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 9 },
  lbl: { fontFamily: '"DM Mono", monospace', fontSize: 10.5, fontWeight: 500, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--aj-text-mid)' },
  val: { fontFamily: '"DM Sans", sans-serif', fontSize: 12, fontWeight: 500 },
  trackWrap: { position: 'relative', height: 18, display: 'flex', alignItems: 'center', touchAction: 'none' },
  track: { position: 'absolute', left: 0, right: 0, height: 3, background: 'var(--aj-border)', borderRadius: 2 },
  fill: { position: 'absolute', left: 0, top: 0, height: 3, borderRadius: 2, transition: 'width 0.15s', pointerEvents: 'none' },
  dotsRow: { position: 'absolute', left: 0, right: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  dot: { borderRadius: '50%', cursor: 'pointer', flexShrink: 0, transition: 'width 0.1s, height 0.1s, background 0.15s' },
}
